import { Component, Signal, effect, computed } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CircleMarkerOptions, PathOptions } from 'leaflet';

import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexYAxis,
    ApexPlotOptions,
    ApexTooltip,
    ApexGrid,
    ApexAnnotations,
    ApexLegend,
} from 'ng-apexcharts';

import { convertNumber } from '../../../../../shared/all.helpers';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec8RedDark,
    colorAec9Cream,
    colorAec7RedLight,
    colorWhite,
    colorLightGreen,
} from 'src/app/shared/all.constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';

import { GenericComponent } from '../generic/generic.component';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
} from 'src/app/shared/components/map/map.constants';
import {
    MV_NETWORKS_AGES_COMPARISON_CHART,
    MV_NETWORKS_AGES_KPIS,
    MV_NETWORKS_AVERAGE_AGES_CHART,
    MV_NETWORKS_CPI_LENGTH_MAP,
    MV_NETWORKS_LOW_SECTION_CHART,
    MV_NETWORKS_OVER_40_CHART,
    MV_NETWORKS_PAPER_CHART,
    MV_NETWORKS_VIGILANCE_POINTS_CHART,
} from '../../assets/keys';

const KEYS = [
    MV_NETWORKS_AGES_KPIS,
    MV_NETWORKS_AVERAGE_AGES_CHART,
    MV_NETWORKS_AGES_COMPARISON_CHART,
    MV_NETWORKS_LOW_SECTION_CHART,
    MV_NETWORKS_PAPER_CHART,
    MV_NETWORKS_OVER_40_CHART,
    MV_NETWORKS_VIGILANCE_POINTS_CHART,
    MV_NETWORKS_CPI_LENGTH_MAP,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    mvNetworksAverageAge?: number;
    mvNetworksAverageAgeEvolution?: number;

    mvNetworksLengthOver40YearsOld?: number;
    mvNetworksLengthOver40YearsOldEvolution?: number;

    mvNetworksLengthCpi?: number;
    mvNetworksLengthCpiEvolution?: number;

    mvNetworksLengthLowSection?: number;
    mvNetworksLengthLowSectionEvolution?: number;
}

interface AverageAgesChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    totalMvNetworkAges?: number;
    overheadMvNetworkAges?: number;
    undergroundCpiMvNetworkAges?: number;
    undergroundSyntheticMvNetworkAges?: number;
    error?: boolean;
}

interface ComparisonChartData {
    id?: number;
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

interface EvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: string;
    length?: number;
    evolution?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    templateUrl: './mv-networks-ages.component.html',
    styleUrls: ['./mv-networks-ages.component.scss'],
})
export class MvNetworksAgesComponent extends GenericComponent {
    public texts = CC_CONSTANTS.mvNetworks.tabs.aging.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_AGES_KPIS),
    ) as Signal<KpiData[]>;

    public averageAgesChartDataSignal: Signal<AverageAgesChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_AVERAGE_AGES_CHART),
    ) as Signal<AverageAgesChartData[]>;
    public averageAgesChartSeries: ApexAxisChartSeries = [];
    public averageAgesChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.mvNetworksAverageAgesChart.yaxisTitleLeft,
            },
        },
    ];
    public averageAgesChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public averageAgesChartGrid: ApexGrid = { padding: { left: 30 } };
    public averageAgesChartError: boolean = true;

    public agesComparisonChartDataSignal: Signal<ComparisonChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_AGES_COMPARISON_CHART),
    ) as Signal<ComparisonChartData[]>;
    public agesComparisonChartSeries: ApexAxisChartSeries = [];
    public agesComparisonChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public agesComparisonCustomTooltip: ApexTooltip['custom'];
    public agesComparisonChartError: boolean = true;
    public agesComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(4),
            borderRadius: 1,
        },
    };
    public comparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };

    public lowSectionChartDataSignal: Signal<EvolutionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_LOW_SECTION_CHART),
    ) as Signal<EvolutionChartData[]>;
    public lowSectionChartSeries: ApexAxisChartSeries = [];
    public lowSectionChartDataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public lowSectionChartError: boolean = false;
    public evolutionChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.mvNetworksLowSectionChart.yaxisTitleLeft,
            },
        },
    ];
    public evolutionPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            columnWidth: '50%',
            barHeight: '70%',
            dataLabels: {
                position: 'center',
            },
        },
    };
    public evolutionChartGrid: ApexGrid = { padding: { right: 50 } };
    public lowSectionCustomTooltip: ApexTooltip['custom'];
    public lowSectionLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec6Gray, colorAec2BlueDark],
        },
    };

    public paperChartDataSignal: Signal<EvolutionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_PAPER_CHART),
    ) as Signal<EvolutionChartData[]>;
    public paperChartSeries: ApexAxisChartSeries = [];
    public paperChartDataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public paperChartError: boolean = true;
    public paperCustomTooltip: ApexTooltip['custom'];
    public paperLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec6Gray, colorAec2BlueDark],
        },
    };

    public over40ChartDataSignal: Signal<EvolutionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_OVER_40_CHART),
    ) as Signal<EvolutionChartData[]>;
    public over40ChartSeries: ApexAxisChartSeries = [];
    public over40ChartDataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public over40ChartError: boolean = true;
    public over40CustomTooltip: ApexTooltip['custom'];
    public over40Legend: ApexLegend = {
        markers: {
            fillColors: [colorAec6Gray, colorAec2BlueDark],
        },
    };

    public vigilancePointsChartDataSignal: Signal<ComparisonChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_VIGILANCE_POINTS_CHART),
    ) as Signal<ComparisonChartData[]>;
    public vigilancePointsChartSeries: ApexAxisChartSeries = [];
    public vigilancePointsChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public vigilancePointsCustomTooltip: ApexTooltip['custom'];
    public vigilancePointsChartError: boolean = true;
    public vigilancePointsPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(3),
            borderRadius: 1,
        },
    };

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_CPI_LENGTH_MAP),
    ) as Signal<any[]>;
    public legendConfig: LegendConfig[] = [
        {
            title: 'Linéaire de CPI HTA',
            values: [
                { color: colorWhite, value: '0 km' },
                { color: colorAec3Green, value: '0 - 1 km' },
                { color: colorLightGreen, value: '1 - 5 km' },
                { color: colorAec4Orange, value: '5 - 10 km' },
                { color: colorAec5Brown, value: '10 - 50 km' },
                { color: colorAec8RedDark, value: '50+ km' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        //GenericComponent intialize sideNav, Tabs (if exists), title and subTitle
        super(route, sidenavService, store);

        effect(() => {
            const averageAgesChartData = this.averageAgesChartDataSignal();
            this.averageAgesChartError = this.checkForError(averageAgesChartData, [
                'xYear',
                'totalMvNetworkAges',
                'overheadMvNetworkAges',
                'undergroundCpiMvNetworkAges',
                'undergroundSyntheticMvNetworkAges',
            ]);
            if (!this.averageAgesChartError) {
                this.averageAgesChartSeries = [
                    {
                        name: this.texts.mvNetworksAverageAgesChart.totalMvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.totalMvNetworkAges,
                        })),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.mvNetworksAverageAgesChart.overheadMvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.overheadMvNetworkAges,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.mvNetworksAverageAgesChart
                            .undergroundCpiMvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.undergroundCpiMvNetworkAges,
                        })),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.mvNetworksAverageAgesChart
                            .undergroundSyntheticMvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.undergroundSyntheticMvNetworkAges,
                        })),
                        color: colorAec1BlueLight,
                    },
                ];
                this.averageAgesChartDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const agesComparisonChartData = this.agesComparisonChartDataSignal();
            this.agesComparisonChartError = this.checkForError(agesComparisonChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.agesComparisonChartError) {
                this.agesComparisonChartSeries = [
                    {
                        name: this.texts.mvNetworksAgesComparisonChart.serieLabel,
                        data: agesComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.mvNetworksAgesComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.mvNetworksAgesComparisonChart.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.mvNetworksAgesComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.mvNetworksAgesComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const lowSectionChartData = this.lowSectionChartDataSignal();
            this.lowSectionChartError = this.checkForError(lowSectionChartData, [
                'xYear',
                'length',
            ]);
            if (!this.lowSectionChartError) {
                const minLowSection =
                    0.5 *
                    lowSectionChartData.reduce(
                        (min, data) => Math.min(min, data.length ?? Infinity),
                        Infinity,
                    );

                this.lowSectionChartSeries = [
                    {
                        name: this.texts.mvNetworksLowSectionChart.axisTitle,
                        type: 'bar',
                        data: lowSectionChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.length,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.mvNetworksLowSectionChart.axisTitleRate,
                        type: 'bar',
                        data: lowSectionChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: minLowSection,
                        })),
                        color: 'transparent',
                    },
                ];
                this.lowSectionChartDataLabels = this.setEvolutionDataLabels(lowSectionChartData);
            }

            const paperChartData = this.paperChartDataSignal();
            this.paperChartError = this.checkForError(paperChartData, ['xYear', 'length']);
            const minPaper =
                0.5 *
                paperChartData.reduce(
                    (min, data) => Math.min(min, data.length ?? Infinity),
                    Infinity,
                );
            if (!this.paperChartError) {
                this.paperChartSeries = [
                    {
                        name: this.texts.mvNetworksPaperChart.axisTitle,
                        type: 'bar',
                        data: paperChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.length,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.mvNetworksPaperChart.axisTitleRate,
                        type: 'bar',
                        data: paperChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: minPaper,
                        })),
                        color: 'transparent',
                    },
                ];
                this.paperChartDataLabels = this.setEvolutionDataLabels(paperChartData);
            }

            const over40ChartData = this.over40ChartDataSignal();
            this.over40ChartError = this.checkForError(over40ChartData, ['xYear', 'length']);
            const minOver40 =
                0.8 *
                over40ChartData.reduce(
                    (min, data) => Math.min(min, data.length ?? Infinity),
                    Infinity,
                );
            if (!this.over40ChartError) {
                this.over40ChartSeries = [
                    {
                        name: this.texts.mvNetworksOver40Chart.axisTitle,
                        type: 'bar',
                        data: over40ChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.length,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.mvNetworksOver40Chart.axisTitleRate,
                        type: 'bar',
                        data: over40ChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: minOver40,
                        })),
                        color: 'transparent',
                    },
                ];
                this.over40ChartDataLabels = this.setEvolutionOver40DataLabels(over40ChartData);
            }

            const vigilancePointsChartData = this.vigilancePointsChartDataSignal();
            this.vigilancePointsChartError = this.checkForError(vigilancePointsChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.vigilancePointsChartError) {
                vigilancePointsChartData.sort((a, b) => a.id! - b.id!);
                this.vigilancePointsChartSeries = [
                    {
                        name: this.texts.mvNetworksVigilancePointsChart.serieLabel,
                        data: vigilancePointsChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.mvNetworksVigilancePointsChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.mvNetworksVigilancePointsChart
                                        .clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.mvNetworksVigilancePointsChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.mvNetworksVigilancePointsChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.lowSectionCustomTooltip = this.setSimpleCustomTooltip(
            this.texts.mvNetworksLowSectionChart.precision,
            [0],
        );

        this.paperCustomTooltip = this.setSimpleCustomTooltip(
            this.texts.mvNetworksPaperChart.precision,
            [0],
        );

        this.over40CustomTooltip = this.setSimpleCustomTooltip(
            this.texts.mvNetworksOver40Chart.precision,
            [0],
        );

        this.agesComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.mvNetworksAgesComparisonChart.precision,
            this.texts.mvNetworksAgesComparisonChart.unitTooltip,
        );
        this.vigilancePointsCustomTooltip = this.setChartCustomTooltip(
            this.texts.mvNetworksVigilancePointsChart.precision,
            this.texts.mvNetworksVigilancePointsChart.unitTooltip,
        );
    }

    public setChartCustomTooltip(
        precision: number | null = null,
        unit: string = ' ',
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} ${unit}</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} ${unit}</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                 <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} ${unit}</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }
    public setEvolutionOver40DataLabels(data: any[]): ApexDataLabels {
        return {
            enabled: true,
            enabledOnSeries: [0, 1],
            formatter: (val: number, opts: any) => {
                const seriesIndex = opts.seriesIndex;
                const dataIndex = opts.dataPointIndex;

                if (seriesIndex === 0) {
                    return `${convertNumber(val, this.texts.mvNetworksOver40Chart.precision)}`;
                }

                if (seriesIndex === 1) {
                    const percentage = data[dataIndex]?.evolution;
                    return `${convertNumber(percentage, this.texts.mvNetworksOver40Chart.ratePrecision)}%`;
                }

                return '';
            },
            textAnchor: 'middle',
            style: {
                colors: [colorAec6Gray, colorAec2BlueDark],
                fontSize: '11px',
            },
            background: {
                enabled: true,
                opacity: 0.5,
                foreColor: 'white',
            },
            distributed: false,
        };
    }

    public setEvolutionDataLabels(data: any[]): ApexDataLabels {
        return {
            enabled: true,
            enabledOnSeries: [0, 1],
            formatter: (val: number, opts: any) => {
                const seriesIndex = opts.seriesIndex;
                const dataIndex = opts.dataPointIndex;

                if (seriesIndex === 0) {
                    return `${convertNumber(val, this.texts.mvNetworksLowSectionChart.precision)}`;
                }

                if (seriesIndex === 1) {
                    const percentage = data[dataIndex]?.evolution;
                    return `${convertNumber(percentage, this.texts.mvNetworksLowSectionChart.ratePrecision)}%`;
                }

                return '';
            },
            textAnchor: 'middle',
            style: {
                colors: [colorAec6Gray, colorAec2BlueDark],
                fontSize: '11px',
            },
            background: {
                enabled: true,
                opacity: 0.5,
                foreColor: 'white',
            },
            distributed: false,
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions | CircleMarkerOptions {
        if (!feature?.properties || feature.properties['cpiLength'] === null) {
            return EmptyPolygonStyle;
        }

        let fillColor = colorWhite;
        const cpiLength = feature.properties['cpiLength'];

        if (cpiLength > 50) {
            fillColor = colorAec8RedDark;
        } else if (cpiLength > 10) {
            fillColor = colorAec5Brown;
        } else if (cpiLength > 5) {
            fillColor = colorAec4Orange;
        } else if (cpiLength > 1) {
            fillColor = colorLightGreen;
        } else if (cpiLength > 0) {
            fillColor = colorAec3Green;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, cpiLength, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const cpiLengthStringified = convertNumber(Number(cpiLength), null);
        return `${cityName} - ${cpiLengthStringified} km`;
    }
}
